import React, { useSyncExternalStore } from 'react';
import type { DerivedGateModule } from '../../utils/derivedGateEquipment';
import { liveOhtRuntimeStore } from '../../store/liveOhtRuntimeStore';
import { useAppSelector } from '../../hooks/redux';
import { useLiveInventoryGates } from '../../hooks/useLiveInventoryDisplay';
import {
  GateStationShell3D,
  type GateStationLedMode,
} from './amhs/GateStation/GateStationShell3D';
import { GateStationLifter3D } from './amhs/GateStation/GateStationLifter3D';

const useRuntimeFrame = () =>
  useSyncExternalStore(
    (notify) => liveOhtRuntimeStore.subscribe(() => notify()),
    () => liveOhtRuntimeStore.getFrame(),
    () => liveOhtRuntimeStore.getFrame()
  );

function resolveLedMode(
  entry: boolean,
  active: boolean,
  gateHoldsFoup: boolean,
  status: string | undefined
): GateStationLedMode {
  if (active) return 'loading';
  if (status && /error/i.test(status)) return 'error';
  if (entry && gateHoldsFoup) return 'ready';
  if (!entry && gateHoldsFoup) return 'ready';
  return 'idle';
}

export function resolveGateStationFoupPresence(args: {
  role: 'entry' | 'exit';
  active: boolean;
  progress01: number;
  pickedUp: boolean;
  dropped: boolean;
  liveGateOccupancy?: number;
  liveGateDelivered?: number;
}): boolean {
  const {
    role,
    active,
    progress01,
    pickedUp,
    dropped,
    liveGateOccupancy = 0,
    liveGateDelivered = 0,
  } = args;
  if (active) {
    return role === 'entry' ? progress01 < 0.75 : progress01 >= 0.15;
  }
  if (role === 'entry') {
    return !pickedUp || liveGateOccupancy > 0;
  }
  return dropped || liveGateDelivered > 0 || liveGateOccupancy > 0;
}

export const DerivedGateEquipment3D: React.FC<{
  module: DerivedGateModule;
}> = ({ module }) => {
  const frame = useRuntimeFrame();
  const running = useAppSelector((state) => state.simulation.running);
  const liveGate = useLiveInventoryGates()?.find(
    (gate) => gate.portId === module.loadPortId
  );
  const runtime = frame.gateByPortId[module.loadPortId];
  const entry = module.role === 'entry';
  const headingRad = (module.headingDeg * Math.PI) / 180;

  const lastPickupEventRef = React.useRef<string | null>(null);
  const droppedFoupRef = React.useRef<boolean>(false);
  const lastRunningRef = React.useRef<boolean>(running);

  if (lastRunningRef.current !== running) {
    lastRunningRef.current = running;
    if (!running) {
      lastPickupEventRef.current = null;
      droppedFoupRef.current = false;
    }
  }

  const active = running && runtime?.status === 'TRANSFERRING';
  const progress01 = runtime?.progress01 ?? 0;

  if (active) {
    if (entry && runtime?.activeEventId) {
      lastPickupEventRef.current = runtime.activeEventId;
    }
    if (!entry && progress01 >= 0.15) {
      droppedFoupRef.current = true;
    }
  }

  const gateHoldsFoup = resolveGateStationFoupPresence({
    role: module.role,
    active,
    progress01,
    pickedUp: lastPickupEventRef.current !== null,
    dropped: droppedFoupRef.current,
    liveGateOccupancy: liveGate?.occupancy,
    liveGateDelivered: liveGate?.delivered,
  });

  const ledMode = resolveLedMode(entry, active, gateHoldsFoup, runtime?.status);

  return (
    <group
      position={[module.x, 0, module.y]}
      rotation={[0, -headingRad, 0]}
      name={`GateStation-${module.role}`}
    >
      {/* Complete Open Container Enclosure */}
      <GateStationShell3D
        entry={entry}
        active={active}
        ledMode={ledMode}
      />

      {/* Dedicated FOUP Lifter Vertical Transfer System */}
      <GateStationLifter3D
        entry={entry}
        active={active}
        gateHoldsFoup={gateHoldsFoup}
        progress01={runtime?.progress01 ?? 0}
        status={runtime?.status}
      />
    </group>
  );
};

export default React.memo(DerivedGateEquipment3D);
